import { useMemo } from "react";
import { formatDistanceToNowStrict, type Locale } from "date-fns";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { StatusBadge } from "@/components/shared/StatusBadge";
import type { StatusBadgeVariant } from "@/components/shared/StatusBadge";
import { getDateFnsLocale } from "@/lib/dateFormat";
import type {
  LastSubmissionItem,
  LastSubmissionStatus,
  LastReceivedInvoiceItem,
} from "@fiscalo/shared-types";

const MAX_ACTIVITY = 5;

function formatElapsedTime(
  dateIso: string,
  locale: Locale,
  justNowLabel: string,
): string {
  const date = new Date(dateIso);
  const now = new Date();
  if (date.getTime() > now.getTime()) {
    return justNowLabel;
  }
  return formatDistanceToNowStrict(date, {
    addSuffix: true,
    locale,
  });
}

function submissionStatusVariant(
  status: LastSubmissionStatus,
): StatusBadgeVariant {
  // DGI-rejected → red "Rechazada" (user must re-issue). Anything else → green "Enviada".
  return status === "rejected" ? "danger" : "success";
}

function submissionStatusLabelKey(status: LastSubmissionStatus): string {
  return status === "rejected"
    ? "invoices.list.statusLabel.rejected"
    : "invoices.list.statusLabel.sent";
}

function submissionStatusFallback(status: LastSubmissionStatus): string {
  return status === "rejected" ? "Rechazada" : "Enviada";
}

type ActivityItem =
  | { kind: "emitted"; data: LastSubmissionItem }
  | { kind: "received"; data: LastReceivedInvoiceItem };

/** Amount without the currency code — the feed renders the code separately. */
function formatAmountOnly(amount: number): string {
  return amount.toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

interface DashboardRecentActivityProps {
  lastSubmissions: LastSubmissionItem[];
  lastReceivedInvoices?: LastReceivedInvoiceItem[];
}

export function DashboardRecentActivity({
  lastSubmissions,
  lastReceivedInvoices,
}: DashboardRecentActivityProps): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const dateLocale = getDateFnsLocale(i18n.language);

  const activityItems = useMemo(() => {
    const emitted: ActivityItem[] = lastSubmissions.map((s) => ({
      kind: "emitted" as const,
      data: s,
    }));

    const received: ActivityItem[] = (lastReceivedInvoices ?? []).map((r) => ({
      kind: "received" as const,
      data: r,
    }));

    const merged = [...emitted, ...received];
    merged.sort((a, b) => {
      const dateA =
        a.kind === "emitted" ? a.data.submittedAt : a.data.createdAt;
      const dateB =
        b.kind === "emitted" ? b.data.submittedAt : b.data.createdAt;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
    return merged.slice(0, MAX_ACTIVITY);
  }, [lastSubmissions, lastReceivedInvoices]);

  if (activityItems.length === 0) {
    return null;
  }

  return (
    <section>
      <div className="fx-section-head">
        <span className="fx-eyebrow">
          {t("dashboard.activity.feedTitle", "Últimas facturas")}
        </span>
        <Link to="/invoices" className="fx-section-head-link">
          {t("dashboard.activity.viewAll", "Ver todas →")}
        </Link>
      </div>
      <div className="fx-feed">
        {activityItems.map((item) => {
          if (item.kind === "emitted") {
            const sub = item.data;
            const isRejected = sub.status === "rejected";
            return (
              <Link
                key={`e-${sub.invoiceId}`}
                to={`/invoices/${sub.invoiceId}`}
                className={`fx-feed-row ${isRejected ? "sev-urgent" : "sev-ok"}`}
              >
                <span className="fx-feed-main">
                  <span className="fx-feed-eyebrow">
                    <span>
                      {t(`invoices.cfeTypes.${sub.cfeType}`, sub.cfeType)}{" "}
                      {sub.internalNumber}
                    </span>
                    <StatusBadge variant={submissionStatusVariant(sub.status)}>
                      {t(
                        submissionStatusLabelKey(sub.status),
                        submissionStatusFallback(sub.status),
                      )}
                    </StatusBadge>
                  </span>
                  <span className="fx-feed-name">
                    {sub.receiverName ??
                      t("dashboard.activity.noReceiver", "Sin destinatario")}
                  </span>
                  <span className="fx-feed-when">
                    {formatElapsedTime(
                      sub.submittedAt,
                      dateLocale,
                      t("dashboard.activity.elapsedJustNow"),
                    )}
                  </span>
                </span>
                {/* Emitted rows previously showed no amount at all while
                      received ones did. The figure now comes from the
                      dashboard payload's totalAmount/currency. */}
                {typeof sub.totalAmount === "number" ? (
                  <span className="fx-feed-amount">
                    <span className="val">
                      {formatAmountOnly(sub.totalAmount)}
                    </span>
                    <span className="cur">{sub.currency ?? "UYU"}</span>
                  </span>
                ) : null}
              </Link>
            );
          }

          const rec = item.data;
          return (
            <Link
              key={`r-${rec.id}`}
              to={`/invoices/recibidas/${rec.id}`}
              className="fx-feed-row sev-neutral"
            >
              <span className="fx-feed-main">
                <span className="fx-feed-eyebrow">
                  <span>
                    {t(`invoices.cfeTypes.${rec.tipoCFE}`, rec.tipoCFE)}{" "}
                    {rec.nro}
                  </span>
                  <StatusBadge variant="info">
                    {t("dashboard.activity.received", "Recibida")}
                  </StatusBadge>
                </span>
                <span className="fx-feed-name">
                  {rec.rucEmisorLegalName ?? rec.rucEmisor}
                </span>
                <span className="fx-feed-when">
                  {formatElapsedTime(
                    rec.createdAt,
                    dateLocale,
                    t("dashboard.activity.elapsedJustNow"),
                  )}
                </span>
              </span>
              <span className="fx-feed-amount">
                <span className="val">{formatAmountOnly(rec.montoTotal)}</span>
                <span className="cur">{rec.moneda}</span>
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
